import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import '@/App.css';
import '@/styles/app-v2.css';
import { AuthProvider } from '@/context/AuthContext';
import { ToastProvider } from '@/context/ToastContext';
import { ProtectedRoute, AdminRoute } from '@/components/ProtectedRoute';
import AppShell from '@/components/AppShell';
import AdminShell from '@/components/AdminShell';

// Pages
import LandingPage from '@/pages/LandingPage';
import AnnoncesPage from '@/pages/AnnoncesPage';
import VerifyPage from '@/pages/VerifyPage';
import SplashPage from '@/pages/SplashPage';
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import DashboardPage from '@/pages/dashboard/DashboardPage';
import ShipmentsPage from '@/pages/shipments/ShipmentsPage';
import ShipmentForm from '@/pages/shipments/ShipmentForm';
import ShipmentDetailPage from '@/pages/shipments/ShipmentDetailPage';
import TripsPage from '@/pages/trips/TripsPage';
import TripForm from '@/pages/trips/TripForm';
import TripDetailPage from '@/pages/trips/TripDetailPage';
import ShipmentMatchingPage from '@/pages/matching/ShipmentMatchingPage';
import TripMatchingPage from '@/pages/matching/TripMatchingPage';
import OffersPage from '@/pages/offers/OffersPage';
import MissionsPage from '@/pages/missions/MissionsPage';
import MissionDetailPage from '@/pages/missions/MissionDetailPage';
import ReceiptPage from '@/pages/missions/ReceiptPage';
import TermsPage from '@/pages/legal/TermsPage';
import MessagingPage from '@/pages/messaging/MessagingPage';
import WalletPage from '@/pages/wallet/WalletPage';
import SubscriptionPage from '@/pages/subscription/SubscriptionPage';
import PayoutsPage from '@/pages/payouts/PayoutsPage';
import ProfilePage from '@/pages/profile/ProfilePage';
import KycPage from '@/pages/kyc/KycPage';
import VerificationsPage from '@/pages/verifications/VerificationsPage';
import SecurityPage from '@/pages/security/SecurityPage';
import PropositionsPage from '@/pages/propositions/PropositionsPage';
import ProposePage from '@/pages/ProposePage';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminModeration from '@/pages/admin/AdminModeration';
import AdminTransactions from '@/pages/admin/AdminTransactions';
import AdminDisputes from '@/pages/admin/AdminDisputes';
import AdminAudit from '@/pages/admin/AdminAudit';
import AdminEmails from '@/pages/admin/AdminEmails';
import AdminRoleRequests from '@/pages/admin/AdminRoleRequests';
import AdminDocs from '@/pages/admin/AdminDocs';
import AdminDesign from '@/pages/admin/AdminDesign';
import RoleRequestPage from '@/pages/role-requests/RoleRequestPage';
import RoleSwitchPage from '@/pages/role-switch/RoleSwitchPage';

function AppWithShell({ children, roles = null }) {
  return (
    <ProtectedRoute requiredRoles={roles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

const SENDER = ['ROLE_SENDER', 'ROLE_ADMIN'];
const CARRIER = ['ROLE_CARRIER', 'ROLE_ADMIN'];

function AdminWithShell({ children }) {
  return (
    <AdminRoute>
      <AdminShell>{children}</AdminShell>
    </AdminRoute>
  );
}

// Ancienne adresse de la page de matching d'un colis (liens et favoris existants)
function LegacyShipmentMatches() {
  const { id } = useParams();
  return <Navigate to={`/app/shipments/${id}/matching`} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <Routes>
            {/* Public */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/annonces" element={<AnnoncesPage />} />
            <Route path="/annonces/:type/:id" element={<ProposePage />} />
            <Route path="/verify/:token" element={<VerifyPage />} />
            <Route path="/demo" element={<SplashPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/conditions" element={<TermsPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* User App */}
            <Route path="/app/dashboard" element={<AppWithShell><DashboardPage /></AppWithShell>} />
            {/* SENDER-only routes */}
            <Route path="/app/shipments" element={<AppWithShell roles={SENDER}><ShipmentsPage /></AppWithShell>} />
            <Route path="/app/shipments/new" element={<AppWithShell roles={SENDER}><ShipmentForm /></AppWithShell>} />
            <Route path="/app/shipments/:id/edit" element={<AppWithShell roles={SENDER}><ShipmentForm /></AppWithShell>} />
            <Route path="/app/shipments/:id/matching" element={<AppWithShell roles={SENDER}><ShipmentMatchingPage /></AppWithShell>} />
            <Route path="/app/shipments/:id/matches" element={<LegacyShipmentMatches />} />
            <Route path="/app/shipments/:id/offers" element={<AppWithShell roles={SENDER}><OffersPage kind="shipment" /></AppWithShell>} />
            <Route path="/app/shipments/:id" element={<AppWithShell><ShipmentDetailPage /></AppWithShell>} />
            {/* CARRIER-only routes */}
            <Route path="/app/trips" element={<AppWithShell roles={CARRIER}><TripsPage /></AppWithShell>} />
            <Route path="/app/my-trips" element={<AppWithShell roles={CARRIER}><TripsPage myTrips /></AppWithShell>} />
            <Route path="/app/trips/new" element={<AppWithShell roles={CARRIER}><TripForm /></AppWithShell>} />
            <Route path="/app/trips/:id/edit" element={<AppWithShell roles={CARRIER}><TripForm /></AppWithShell>} />
            <Route path="/app/trips/:id/matching" element={<AppWithShell roles={CARRIER}><TripMatchingPage /></AppWithShell>} />
            <Route path="/app/trips/:id/offers" element={<AppWithShell roles={CARRIER}><OffersPage kind="trip" /></AppWithShell>} />
            <Route path="/app/trips/:id" element={<AppWithShell><TripDetailPage /></AppWithShell>} />
            <Route path="/app/search-shipments" element={<AppWithShell roles={CARRIER}><ShipmentsPage /></AppWithShell>} />
            <Route path="/app/search-trips" element={<AppWithShell roles={SENDER}><TripsPage /></AppWithShell>} />
            <Route path="/app/missions" element={<AppWithShell><MissionsPage /></AppWithShell>} />
            <Route path="/app/missions/:id" element={<AppWithShell><MissionDetailPage /></AppWithShell>} />
            <Route path="/app/missions/:id/recu" element={<AppWithShell><ReceiptPage /></AppWithShell>} />
            <Route path="/app/messages" element={<AppWithShell><MessagingPage /></AppWithShell>} />
            <Route path="/app/messages/:id" element={<AppWithShell><MessagingPage /></AppWithShell>} />
            <Route path="/app/wallet" element={<AppWithShell><WalletPage /></AppWithShell>} />
            <Route path="/app/abonnement" element={<AppWithShell roles={CARRIER}><SubscriptionPage /></AppWithShell>} />
            <Route path="/app/versements" element={<AppWithShell roles={CARRIER}><PayoutsPage /></AppWithShell>} />
            <Route path="/app/profile" element={<AppWithShell><ProfilePage /></AppWithShell>} />
            <Route path="/app/securite" element={<AppWithShell><SecurityPage /></AppWithShell>} />
            <Route path="/app/profile/:id" element={<AppWithShell><ProfilePage /></AppWithShell>} />
            <Route path="/app/kyc" element={<AppWithShell><KycPage /></AppWithShell>} />
            <Route path="/app/verifications" element={<AppWithShell><VerificationsPage /></AppWithShell>} />
            <Route path="/app/propositions" element={<AppWithShell><PropositionsPage /></AppWithShell>} />
            <Route path="/app/role-requests" element={<AppWithShell><RoleRequestPage /></AppWithShell>} />
            <Route path="/app/switch-role" element={<AppWithShell><RoleSwitchPage /></AppWithShell>} />
            <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />

            {/* Admin */}
            <Route path="/admin/dashboard" element={<AdminWithShell><AdminDashboard /></AdminWithShell>} />
            <Route path="/admin/users" element={<AdminWithShell><AdminUsers /></AdminWithShell>} />
            <Route path="/admin/moderation" element={<AdminWithShell><AdminModeration /></AdminWithShell>} />
            <Route path="/admin/transactions" element={<AdminWithShell><AdminTransactions /></AdminWithShell>} />
            <Route path="/admin/disputes" element={<AdminWithShell><AdminDisputes /></AdminWithShell>} />
            <Route path="/admin/audit" element={<AdminWithShell><AdminAudit /></AdminWithShell>} />
            <Route path="/admin/emails" element={<AdminWithShell><AdminEmails /></AdminWithShell>} />
            <Route path="/admin/role-requests" element={<AdminWithShell><AdminRoleRequests /></AdminWithShell>} />
            <Route path="/admin/docs" element={<AdminWithShell><AdminDocs /></AdminWithShell>} />
            <Route path="/admin/design" element={<AdminWithShell><AdminDesign /></AdminWithShell>} />
            <Route path="/admin/securite" element={<AdminWithShell><SecurityPage /></AdminWithShell>} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
